import { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import AddressPartFields from "./AddressPartFields";
import PhonePartFields from "./PhonePartFields";
import {
  AGENT_TYPE_OPTIONS,
  TITLE_OPTIONS,
  emptyMaraForm,
  toMaraForm,
  toMaraRequest,
} from "./maraAgentFormModel";
import CloseIcon from "@mui/icons-material/Close";

export const SCOPE_OPTIONS = [
  { value: "VISA", label: "Visa" },
  { value: "INSURANCE", label: "Insurance" },
  { value: "RPL", label: "RPL" },
  { value: "ADMISSION", label: "Admission" },
  { value: "SKILL_ASSESSMENT", label: "Skill Assessment" },
];

const MaraAgentFormDialog = ({ open, onClose, agent, onSubmit, submitting }) => {
  const [form, setForm] = useState(emptyMaraForm);

  useEffect(() => {
    if (open) setForm(toMaraForm(agent));
  }, [open, agent]);

  const handleChange = (field) => (event) =>
    setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const handlePart = (field) => (part) => setForm((prev) => ({ ...prev, [field]: part }));

  const toggleTag = (tag) => {
    setForm((prev) => ({
      ...prev,
      scopes: prev.scopes.includes(tag)
        ? prev.scopes.filter((t) => t !== tag)
        : [...prev.scopes, tag],
    }));
  };

  const isValid = form.agentName.trim() && form.scopes.length > 0;

  const handleSubmit = () => {
    if (!isValid) return;
    onSubmit(toMaraRequest(form));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle className="flex items-center justify-between">
        <span className="font-semibold">{agent ? "Edit MARA agent" : "Add MARA agent"}</span>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mt: 0.25 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Consultant Agent"
              size="small"
              fullWidth
              value={form.consultantAgent}
              onChange={handleChange("consultantAgent")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Agent Name"
              size="small"
              fullWidth
              required
              value={form.agentName}
              onChange={handleChange("agentName")}
            />
          </Grid>
          <Grid item xs={12} sm={form.title === "OTHER" ? 3 : 6}>
            <TextField
              select
              label="Title"
              size="small"
              fullWidth
              value={form.title}
              onChange={handleChange("title")}
            >
              <MenuItem value="">Not set</MenuItem>
              {TITLE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {form.title === "OTHER" && (
            <Grid item xs={12} sm={3}>
              <TextField
                label="Title (other)"
                size="small"
                fullWidth
                value={form.titleOther}
                onChange={handleChange("titleOther")}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Registered as"
              size="small"
              fullWidth
              value={form.agentType}
              onChange={handleChange("agentType")}
              helperText="Decides which registration number government forms ask for"
            >
              <MenuItem value="">Not set</MenuItem>
              {AGENT_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {form.agentType !== "LEGAL_PRACTITIONER" && (
            <Grid item xs={12} sm={6}>
              <TextField
                label="MARA Registration No. (MARN)"
                size="small"
                fullWidth
                value={form.maraRegistrationNo}
                onChange={handleChange("maraRegistrationNo")}
              />
            </Grid>
          )}
          {form.agentType === "LEGAL_PRACTITIONER" && (
            <Grid item xs={12} sm={6}>
              <TextField
                label="Legal Practitioner Number (LPN)"
                size="small"
                fullWidth
                value={form.lpn}
                onChange={handleChange("lpn")}
                helperText="Last 5 digits; forms print the leading 5 5 themselves"
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Email Address"
              size="small"
              fullWidth
              type="email"
              value={form.email}
              onChange={handleChange("email")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Mobile No."
              size="small"
              fullWidth
              value={form.mobile}
              onChange={handleChange("mobile")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Phone"
              size="small"
              fullWidth
              value={form.phone}
              onChange={handleChange("phone")}
            />
          </Grid>
          <PhonePartFields
            label="Office hours"
            value={form.officePhone}
            onChange={handlePart("officePhone")}
          />
          <Grid item xs={12}>
            <TextField
              label="Business Address"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={form.businessAddress}
              onChange={handleChange("businessAddress")}
              helperText="Free-text summary used around the app"
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" fontWeight={700}>
              Business or residential address
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Printed box by box on government forms such as the 956.
            </Typography>
          </Grid>
          <AddressPartFields value={form.address} onChange={handlePart("address")} />
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={form.usesCorrespondenceAddress}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      usesCorrespondenceAddress: event.target.checked,
                    }))
                  }
                />
              }
              label="Has a separate correspondence address?"
            />
            {!form.usesCorrespondenceAddress && (
              <Typography variant="caption" color="text.secondary" display="block">
                Forms will print AS ABOVE for the correspondence address.
              </Typography>
            )}
          </Grid>
          {form.usesCorrespondenceAddress && (
            <AddressPartFields
              value={form.correspondenceAddress}
              onChange={handlePart("correspondenceAddress")}
            />
          )}
          <Grid item xs={12} sm={6}>
            <TextField
              label="State / Territory"
              size="small"
              fullWidth
              value={form.state}
              onChange={handleChange("state")}
              helperText="Autofills the client agreement's governing state/territory"
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              Services this agent provides
            </Typography>
            <FormGroup row>
              {SCOPE_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      size="small"
                      checked={form.scopes.includes(option.value)}
                      onChange={() => toggleTag(option.value)}
                    />
                  }
                  label={option.label}
                />
              ))}
            </FormGroup>
            {form.scopes.length === 0 && (
              <Typography variant="caption" color="error">
                Select at least one service.
              </Typography>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!isValid || submitting}>
          {submitting ? "Saving…" : agent ? "Save changes" : "Add agent"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MaraAgentFormDialog;
